import { useEffect, useState } from 'react';
import { useRouter } from '@tanstack/react-router';
import {
  AlertTriangle,
  Check,
  CheckCircle2,
  Copy,
  QrCode,
  ShieldCheck,
} from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { api, ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';

/**
 * /auth/2fa-required — AK-20: принудительная настройка 2FA для админских ролей.
 *
 * Standalone-страница (БЕЗ AppShell). Earth Tech градиент + Steps (1.Начать → 2.QR → 3.Recovery).
 * Логика 1:1 с frontend/src/app/auth/2fa-required/page.tsx (AntD).
 */

interface Setup {
  secret: string;
  qr_data_url: string;
  provisioning_uri: string;
}

const STEP_LABELS = ['Начать', 'Сканировать QR', 'Recovery-коды'];

function Steps({ current }: { current: number }) {
  return (
    <ol className="flex items-center gap-2 text-sm">
      {STEP_LABELS.map((label, idx) => {
        const done = idx < current;
        const active = idx === current;
        return (
          <li key={label} className="flex items-center gap-2">
            <span
              className={cn(
                'flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold border',
                done && 'bg-primary text-primary-foreground border-primary',
                active &&
                  !done &&
                  'border-primary text-primary',
                !done && !active && 'border-border text-muted-foreground',
              )}
            >
              {done ? <Check className="h-3.5 w-3.5" /> : idx + 1}
            </span>
            <span
              className={cn(
                'whitespace-nowrap',
                active ? 'text-foreground font-medium' : 'text-muted-foreground',
              )}
            >
              {label}
            </span>
            {idx < STEP_LABELS.length - 1 && (
              <span className="mx-1 h-px w-8 bg-border" />
            )}
          </li>
        );
      })}
    </ol>
  );
}

export function TwoFARequiredPage() {
  const router = useRouter();
  const [step, setStep] = useState(0);
  const [setup, setSetup] = useState<Setup | null>(null);
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const me = await api.get<{
          user: { email: string };
          must_setup_2fa?: boolean;
        }>('/api/v1/auth/me');
        setEmail(me.user.email);
        if (me.must_setup_2fa === false) {
          router.navigate({ to: '/dashboard' });
        }
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          window.location.href = '/login';
        }
      }
    })();
  }, [router]);

  const beginSetup = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const r = await api.post<Setup>('/api/v1/auth/2fa/setup');
      setSetup(r);
      setStep(1);
    } catch (err) {
      if (err instanceof ApiError) setError(err.detail);
    } finally {
      setSubmitting(false);
    }
  };

  const confirmCode = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const r = await api.post<{ enabled: boolean; recovery_codes: string[] }>(
        '/api/v1/auth/2fa/verify-setup',
        { code: code.trim() },
      );
      setRecoveryCodes(r.recovery_codes);
      setStep(2);
      setInfo('2FA включена');
    } catch (err) {
      if (err instanceof ApiError) setError(err.detail);
    } finally {
      setSubmitting(false);
    }
  };

  const finish = async () => {
    try {
      const me = await api.get<{
        user: { platform_role?: string | null };
        memberships?: unknown[];
      }>('/api/v1/auth/me');
      const hasPA = !!me.user.platform_role;
      const mbs = me.memberships ?? [];
      if (hasPA && mbs.length) window.location.href = '/auth/select-context';
      else if (hasPA) window.location.href = '/platform-admin';
      else window.location.href = '/dashboard';
    } catch {
      window.location.href = '/dashboard';
    }
  };

  const copyCodes = () => {
    if (!recoveryCodes) return;
    navigator.clipboard.writeText(recoveryCodes.join('\n'));
    setInfo('Скопировано');
  };

  const logout = async () => {
    try {
      await api.post('/api/v1/auth/logout');
    } catch {
      /* ignore */
    }
    window.location.href = '/login';
  };

  return (
    <div
      className="min-h-dvh flex items-center justify-center p-6"
      style={{
        background:
          'linear-gradient(135deg, #0B1F1A 0%, #102A23 60%, #1F6F4A 100%)',
      }}
    >
      <Card className="max-w-2xl w-full">
        <CardContent className="pt-6 space-y-4">
          <Alert variant="warning">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>
              Обязательная настройка двухфакторной аутентификации
            </AlertTitle>
            <AlertDescription>
              Ваша роль требует 2FA для безопасного доступа. Настройте её сейчас —
              пропустить нельзя.
            </AlertDescription>
          </Alert>

          <div>
            <h1 className="text-xl font-semibold flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-primary" />
              Настройка 2FA
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Аккаунт: <strong className="text-foreground">{email || '…'}</strong>
            </p>
          </div>

          <Steps current={step} />

          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {info && !error && (
            <Alert variant="success">
              <CheckCircle2 className="h-4 w-4" />
              <AlertDescription>{info}</AlertDescription>
            </Alert>
          )}

          {step === 0 && (
            <div className="space-y-3">
              <p className="text-sm">
                Установите приложение-аутентификатор на телефон:{' '}
                <strong>Google Authenticator</strong>, <strong>Authy</strong>,{' '}
                <strong>Microsoft Authenticator</strong> или{' '}
                <strong>1Password</strong>.
              </p>
              <Button
                size="lg"
                disabled={submitting}
                onClick={beginSetup}
                className="gap-2"
              >
                <QrCode className="h-4 w-4" />
                {submitting ? 'Готовим…' : 'Начать настройку'}
              </Button>
            </div>
          )}

          {step === 1 && setup && (
            <div className="space-y-4">
              <p className="text-sm">
                Откройте приложение-аутентификатор и отсканируйте QR-код:
              </p>
              <div className="text-center p-4 bg-white border border-border rounded-lg">
                <img
                  src={setup.qr_data_url}
                  alt="QR"
                  className="mx-auto max-w-[240px]"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Не работает камера? Введите ключ вручную:{' '}
                <code className="bg-muted px-1.5 py-0.5 rounded text-xs">
                  {setup.secret}
                </code>
              </p>
              <div className="space-y-1.5">
                <Label htmlFor="totp-code">6-значный код из приложения</Label>
                <Input
                  id="totp-code"
                  autoFocus
                  placeholder="123456"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') confirmCode();
                  }}
                  maxLength={6}
                  className="h-11 text-lg text-center tracking-[0.4em]"
                />
                <p className="text-xs text-muted-foreground">
                  Код обновляется каждые 30 секунд
                </p>
              </div>
              <Button size="lg" disabled={submitting} onClick={confirmCode}>
                {submitting ? 'Проверяем…' : 'Подтвердить'}
              </Button>
            </div>
          )}

          {step === 2 && recoveryCodes && (
            <div className="space-y-4">
              <Alert variant="warning">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Сохраните эти коды!</AlertTitle>
                <AlertDescription>
                  Recovery-коды нужны если потеряете доступ к приложению. Каждый код
                  одноразовый. После нажатия «Готово» коды не будут показаны снова.
                </AlertDescription>
              </Alert>
              <div className="bg-muted p-4 rounded-lg font-mono text-sm grid grid-cols-2 gap-2">
                {recoveryCodes.map((c) => (
                  <div key={c}>{c}</div>
                ))}
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button variant="outline" onClick={copyCodes} className="gap-2">
                  <Copy className="h-4 w-4" />
                  Скопировать все
                </Button>
                <Button size="lg" onClick={finish} className="gap-2">
                  <CheckCircle2 className="h-4 w-4" />
                  Готово — войти в систему
                </Button>
              </div>
            </div>
          )}

          <Separator />
          <div className="text-right">
            <Button variant="ghost" size="sm" onClick={logout}>
              Выйти
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default TwoFARequiredPage;
